/**
 * Zeitraum-Mathematik fuer Ziele.
 *
 * Rein und ohne React oder Dexie - deshalb vollstaendig testbar. Hier liegen
 * die Faelle, die man in einer Komponente garantiert falsch macht: ein
 * 12-Wochen-Zyklus laeuft ueber den Jahreswechsel, Quartale sind
 * unterschiedlich lang, und ein Zeitraum von einem einzigen Tag darf nicht
 * durch null teilen.
 *
 * Konvention: `end` ist INKLUSIV. Das weicht bewusst von den ganztaegigen
 * Kalenderterminen ab (die exklusiv enden, iCal-Konvention), weil ein Ziel
 * kein Termin ist und "laeuft bis einschliesslich 31.12." genau das ist, was
 * im Formular steht.
 */
import { addDays, addWeeks, differenceInDays } from '@/lib/datetime';
import type { GoalHorizon, LocalDate } from '@/domain/types';

export interface Period {
  start: LocalDate;
  /** Inklusiv - der letzte Tag, an dem das Ziel laeuft. */
  end: LocalDate;
}

export type Quarter = 1 | 2 | 3 | 4;

/** Ein 12-Wochen-Zyklus nach "12 Week Year". */
export const TWELVE_WEEK_LENGTH = 12;

const pad2 = (n: number) => String(n).padStart(2, '0');

export function yearPeriod(year: number): Period {
  return { start: `${year}-01-01`, end: `${year}-12-31` };
}

export function quarterPeriod(year: number, quarter: Quarter): Period {
  const firstMonth = (quarter - 1) * 3 + 1;
  const start: LocalDate = `${year}-${pad2(firstMonth)}-01`;
  // Erster Tag des Folgequartals minus ein Tag - trifft Schaltjahre von selbst.
  const afterYear = quarter === 4 ? year + 1 : year;
  const afterMonth = quarter === 4 ? 1 : firstMonth + 3;
  const end = addDays(`${afterYear}-${pad2(afterMonth)}-01` as LocalDate, -1);
  return { start, end };
}

/** Zwoelf Wochen ab `start`, inklusiv - also 84 Tage. */
export function twelveWeekPeriod(start: LocalDate): Period {
  return { start, end: addDays(addWeeks(start, TWELVE_WEEK_LENGTH), -1) };
}

export function quarterOf(date: LocalDate): Quarter {
  return (Math.floor((+date.slice(5, 7) - 1) / 3) + 1) as Quarter;
}

export function yearOf(date: LocalDate): number {
  return +date.slice(0, 4);
}

/**
 * Vorbelegung im Formular: der Zeitraum, der zum gewaehlten Horizont passt
 * und `anchor` enthaelt. Ueberschreibbar - es ist ein Vorschlag, keine Regel.
 */
export function periodForHorizon(horizon: GoalHorizon, anchor: LocalDate): Period {
  switch (horizon) {
    case 'jahr':
      return yearPeriod(yearOf(anchor));
    case 'quartal':
      return quarterPeriod(yearOf(anchor), quarterOf(anchor));
    case '12wochen':
      return twelveWeekPeriod(anchor);
  }
}

/** Laenge in Tagen, inklusiv beider Enden. Nie kleiner als 1. */
export function periodLength(period: Period): number {
  return Math.max(1, differenceInDays(period.start, period.end) + 1);
}

export function containsDate(period: Period, date: LocalDate): boolean {
  return date >= period.start && date <= period.end;
}

/**
 * Anteil der verstrichenen Zeit, 0..1.
 *
 * Gezaehlt werden die Tage bis EINSCHLIESSLICH heute: am ersten Tag ist ein
 * Tag von N angebrochen, am letzten Tag sind es alle N. Vor dem Start 0, nach
 * dem Ende 1.
 *
 * Das ist die Zahl, gegen die der Zielfortschritt verglichen wird - "40 %
 * geschafft" sagt erst etwas aus, wenn man weiss, dass 65 % der Zeit um sind.
 */
export function periodProgress(period: Period, today: LocalDate): number {
  const elapsed = differenceInDays(period.start, today) + 1;
  return clamp01(elapsed / periodLength(period));
}

/** Verbleibende Tage einschliesslich heute. Nach dem Ende 0. */
export function daysRemaining(period: Period, today: LocalDate): number {
  return Math.max(0, differenceInDays(today, period.end) + 1);
}

export function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}
